/**
 * Тема оформления (CLIENT_WEB.md §3.7).
 *
 * Единственный разрешённый ключ в localStorage — `theme` (§1.4):
 * значение нужно синхронно до первой отрисовки, а IndexedDB асинхронна.
 * Между устройствами тема не синхронизируется (GAP-ALL-06).
 */
import { create } from 'zustand'

import { refreshThemeColor } from './theme-color'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    /* приватный режим */
  }
  return 'system'
}

function systemTheme(): ResolvedTheme {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference
}

/**
 * §3.7 п. 2, п. 4: атрибут на <html> и обновление meta[theme-color].
 * Цвет системной области считается от шапки текущего экрана (theme-color.ts),
 * поэтому здесь только просьба пересчитать его под новую тему.
 */
function applyTheme(preference: ThemePreference): void {
  const resolved = resolveTheme(preference)
  const root = document.documentElement

  root.setAttribute('data-theme', resolved)
  root.setAttribute('data-theme-preference', preference)
  root.removeAttribute('data-theme-pending')

  refreshThemeColor()
}

interface ThemeState {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
  syncFromSystem: () => void
}

export const useThemeStore = create<ThemeState>((set) => {
  const initial = readPreference()

  return {
    preference: initial,
    resolved: resolveTheme(initial),

    setPreference: (preference) => {
      try {
        localStorage.setItem(STORAGE_KEY, preference)
      } catch {
        /* приватный режим — тема действует до перезагрузки */
      }
      applyTheme(preference)
      set({ preference, resolved: resolveTheme(preference) })
    },

    syncFromSystem: () =>
      set((state) => (state.preference === 'system' ? { resolved: systemTheme() } : state)),
  }
})

/** Подписка на смену системной темы — активна только при значении «системная». */
export function watchSystemTheme(): () => void {
  const list = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
  if (!list) return () => undefined

  const handler = (): void => {
    const { preference, syncFromSystem } = useThemeStore.getState()
    if (preference !== 'system') return
    applyTheme('system')
    syncFromSystem()
  }

  list.addEventListener('change', handler)
  return () => list.removeEventListener('change', handler)
}
